import { Checkbox as MuiCheckbox, type CheckboxProps } from "@mui/material";
import { styled } from "@mui/material/styles";

const Icon = styled("span")(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 8,
    width: 32,
    height: 32,
    boxSizing: "border-box",
    position: "relative",

    "&::before": {
      content: "''",
      display: "block",
      width: 16,
      height: 16,
      background: isDark ? "#2a2b2c" : "#FFFFFF",
      borderRadius: 4,
      boxShadow: isDark
        ? "0px 0px 0px 1px rgba(255, 255, 255, 0.18)"
        : `0px 1px 1px rgba(0, 0, 0, 0.1),
           0px 0px 0px 1px rgba(70, 79, 96, 0.16),
           0px 2px 5px rgba(89, 96, 120, 0.1)`,
      transition: "all 0.2s ease",
    },

    ".Mui-disabled &::before": {
      background: isDark ? "#1b1b1c" : "#f5f5f5",
      border: isDark ? "1px solid #323232" : "1px solid #d0d0d0",
      boxShadow: "none",
    },
  };
});

const CheckedIcon = styled(Icon)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  // Dark mode: bright neutral fill with dark check mark (no blue).
  // Light mode: keeps the original blue brand fill.
  const checkedBg = isDark ? "#f3f4f6" : "#33628B";
  const checkedRing = isDark ? "#f3f4f6" : "#33628B";
  const checkedInsetHighlight = isDark ? "#ffffff" : "#497DAA";
  const checkmarkColor = isDark ? "#111827" : "#FFFFFF";
  return {
    "&::before": {
      background: checkedBg,
      boxShadow: `
        0px 1px 1px rgba(0, 0, 0, 0.14),
        0px 0px 0px 1px ${checkedRing},
        0px 2px 5px ${isDark ? "rgba(0, 0, 0, 0.4)" : "rgba(51, 98, 139, 0.12)"},
        inset 0px 1px 0px ${checkedInsetHighlight}
      `,
    },
    "&::after": {
      content: "''",
      position: "absolute",
      width: 6,
      height: 11,
      display: "block",
      top: "48%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(45deg)",
      borderBottom: `2px solid ${checkmarkColor}`,
      borderRight: `2px solid ${checkmarkColor}`,
      boxSizing: "border-box",
    },

    ".Mui-disabled &::before": {
      background: isDark ? "#404040" : "#b3c7d9",
      boxShadow: "none",
      border: isDark ? "1px solid #555555" : "1px solid #a1b6cb",
    },
    ".Mui-disabled &::after": {
      borderBottom: isDark
        ? "2px solid rgba(243, 244, 246, 0.65)"
        : "2px solid rgba(255, 255, 255, 0.8)",
      borderRight: isDark
        ? "2px solid rgba(243, 244, 246, 0.65)"
        : "2px solid rgba(255, 255, 255, 0.8)",
    },
  };
});

export function Checkbox(props: CheckboxProps) {
  return (
    <MuiCheckbox
      disableRipple
      icon={<Icon />}
      checkedIcon={<CheckedIcon />}
      sx={{
        p: 0,
        opacity: props.disabled ? 0.7 : 1,
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "opacity 0.2s ease",
      }}
      {...props}
    />
  );
}
